import { BRAND_OG_IMAGE_URL, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import profileImage from "@/assets/profile-delfu.jpg";
import reel1 from "@/assets/reel-1.jpg";
import reel2 from "@/assets/reel-2.jpg";
import reel3 from "@/assets/reel-3.jpg";
import { BaseTemplate } from "@/components/templates/BaseTemplate";
import { BioLinkTemplate } from "@/components/templates/BioLinkTemplate";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useSEO } from "@/hooks/useSEO";

type MediaItem = {
  id: number;
  type: "video" | "photo";
  src: string;
  embedId?: string;  // For Streamable videos
  cloudflareId?: string;  // For Cloudflare Stream videos
  title: string;
};

const mediaItems: MediaItem[] = [
  { 
    id: 1, 
    type: "video", 
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/55af92c0a6afac58e780413127c73c5c/thumbnails/thumbnail.jpg", 
    cloudflareId: "55af92c0a6afac58e780413127c73c5c", 
    title: "" 
  },
  { 
    id: 2, 
    type: "video", 
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/27cbea077c11d9cc0569774b8c443a04/thumbnails/thumbnail.jpg", 
    cloudflareId: "27cbea077c11d9cc0569774b8c443a04", 
    title: "" 
  },
  { 
    id: 3, 
    type: "video", 
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/55af92c0a6afac58e780413127c73c5c/thumbnails/thumbnail.jpg", 
    cloudflareId: "55af92c0a6afac58e780413127c73c5c", 
    title: "" 
  },
  {
    id: 4,
    type: "photo",
    src: reel1,
    title: "TikToker Lifestyle"
  },
  {
    id: 5,
    type: "photo",
    src: reel2,
    title: "Behind the TikToks"
  },
  {
    id: 6,
    type: "photo",
    src: profileImage,
    title: "TikToker Portrait"
  },
  {
    id: 7,
    type: "video",
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/27cbea077c11d9cc0569774b8c443a04/thumbnails/thumbnail.jpg",
    cloudflareId: "27cbea077c11d9cc0569774b8c443a04",
    title: ""
  },
  {
    id: 8,
    type: "photo",
    src: reel3,
    title: "TikTok Creator"
  },
  {
    id: 9,
    type: "photo",
    src: "https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/e9f2e4fc-fbf1-4ead-6e36-c8d2b9917d00/public",
    title: "TikTok Lifestyle"
  },
  {
    id: 10,
    type: "photo",
    src: "https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/5693d23a-20a6-4e89-9cb9-806ca3ccc900/public",
    title: "Viral Creator"
  },
  {
    id: 11,
    type: "photo",
    src: "https://imagedelivery.net/N5r9mtOgBIolYeX8w64oqg/2fe5d4c8-fefd-49a3-c53b-6736465a1b00/public",
    title: "TikTok Studio"
  },
  {
    id: 12,
    type: "video",
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/0599f80309c4103f7ebe835cf09f5a41/thumbnails/thumbnail.jpg",
    cloudflareId: "0599f80309c4103f7ebe835cf09f5a41",
    title: ""
  },
  {
    id: 13,
    type: "video",
    src: "https://customer-zxw1qxgyoag8tcec.cloudflarestream.com/905a888c2c2c99b75ac5c03f3ab98aaa/thumbnails/thumbnail.jpg",
    cloudflareId: "905a888c2c2c99b75ac5c03f3ab98aaa",
    title: ""
  },
];

export const Tiktoker = () => {
  const { region, shouldUseBaseTemplate, shouldUseBioLinkTemplate } = useTemplateSelection();

  // Enhanced SEO configuration with TikToker focus
  const seoConfig = {
    title: region === "US" 
      ? `${BRAND_PERSON_NAME} | TikToker & Viral Content Creator - Join My Community`
      : `${BRAND_PERSON_NAME} - TikToker Argentina | Contenido Viral y Trends`,
    description: region === "US" 
      ? `Join TikToker ${BRAND_PERSON_NAME}'s exclusive community! Get access to premium content, join our Telegram group, and connect directly. Your gateway to exclusive TikTok behind-the-scenes content.`
      : `${BRAND_PERSON_NAME} TikToker argentina. Contenido viral, trends, challenges y entretenimiento auténtico. Sígueme para videos diarios y colaboraciones exclusivas.`,
    keywords: region === "US"
      ? `${BRAND_PERSON_NAME}, TikToker, viral content, TikTok creator, premium content, exclusive access, dance trends, lifestyle TikTok, content creator, community`
      : `tiktoker, ${BRAND_PERSON_NAME} tiktoker, argentina tiktoker, contenido viral, tiktok trends, dance, challenges, videos virales, creadora contenido, tiktok argentina`,
    ogImage: BRAND_OG_IMAGE_URL,
    ogTitle: region === "US"
      ? `${BRAND_PERSON_NAME} | TikToker & Viral Content Creator - Join My Community`
      : `${BRAND_PERSON_NAME} - TikToker Argentina`,
    ogDescription: region === "US"
      ? "Join my exclusive community! Premium TikTok content, Telegram group, and direct connection. Let's create viral content together! 🎵✨"
      : "TikToker argentina con contenido viral auténtico. Trends, challenges y entretenimiento que inspira a miles de personas.",
    canonical: `${BRAND_SITE_URL}/tiktoker`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
    type: region === "US" ? "profile" : "website",
    locale: region === "US" ? "en_US" : "es_AR",
    alternateLocale: region === "US" ? "es_AR" : "en_US",
  };

  useSEO(seoConfig);

  // Use template based on feature flags
  if (shouldUseBioLinkTemplate()) {
    return <BioLinkTemplate mediaItems={mediaItems} />;
  }

  // Default to BaseTemplate (LATAM style)
  return <BaseTemplate mediaItems={mediaItems} />;
};

export default Tiktoker;